// ABOUTME: Keeps the tail of an agent turn that spoke UNDER the intro film from becoming audible
// ABOUTME: the instant the film ends — the "welcome cuts off mid-sentence and then resumes" bug.
//
// The incident (2026-09-08, watches demo)
// ---------------------------------------
// `handleAudioPlayback` drops agent audio while the film is on screen, and that is the whole of the
// "the agent is never heard over the film" guarantee. It is per CHUNK, not per TURN, so it only
// holds while `showIntroRef` is true. Measured against the local stack: the model greets under the
// film despite being told to stay silent, and on a slow run it is still mid-monologue when the 10 s
// film ends —
//
//   12.56s  agent (under the film) "...Many collectors are drawn to our watches for their..."
//   12.67s  client sends intro_finished
//   12.74s  film ends, showIntro flips false      <- every remaining chunk of THAT turn now plays
//   14.95s  turnComplete                          <- the guest has heard ~2 s of a stray sentence
//   15.27s  interrupted (the intro_finished injection barges in)
//   15.53s  the real welcome starts
//
// so the guest hears a fragment beginning mid-sentence, a hard cut, then a different welcome — with
// nothing acknowledging what was lost. Both halves are needed to reproduce: a turn still generating
// at film end, and the injected welcome that cuts it.
//
// The rule: once a chunk has been dropped because the film was up, keep dropping that TURN's audio
// until the turn ends. The exit is the turn boundary (`turnComplete`, or the `interrupted` that the
// `intro_finished` injection itself provokes) — the ceiling below is only a backstop.

/**
 * Backstop for a boundary that never arrives, in ms since the last chunk dropped under the film.
 *
 * Deliberately short, and the reason is the same asymmetry `useManualStop` documents: holding too
 * long swallows the real welcome (catastrophic, no symptom), holding too short leaks a fragment of
 * the stray turn (cosmetic — the bug we already had). The measured tail is ~2 s and the boundary is
 * effectively guaranteed, since ADK closes every turn and the injection forces an interrupt, so in
 * practice this never fires.
 */
export const FILM_TAIL_CEILING_MS = 4000;

/**
 * Does this raw ADK event end the turn?
 *
 * `turnComplete` is the ordinary close (see `useManualStop.isTurnComplete` for the by_alias wire
 * shape). `interrupted` is the other one, and it matters here specifically: the `intro_finished`
 * welcome injection reads as a barge-in, so ADK cuts the under-film turn with `interrupted` and
 * nothing of it can follow. The backend relays events in order, so once either has arrived, no more
 * of that turn's audio is on the wire.
 */
export function isTurnBoundary(msg) {
  if (!msg || typeof msg !== 'object') return false;
  return msg.turnComplete === true
    || msg.serverContent?.turnComplete === true
    || msg.interrupted === true;
}

/**
 * @param {object}   [p]
 * @param {Function} [p.now]        injectable clock, for the ceiling's tests
 * @param {number}   [p.ceilingMs]  see FILM_TAIL_CEILING_MS
 */
export function createFilmTailGate({ now = Date.now, ceilingMs = FILM_TAIL_CEILING_MS } = {}) {
  // Deadline in `now`'s clock up to which this turn's audio is dropped; 0 means "gate open".
  // A deadline rather than a boolean so the backstop needs no timer to leak or clean up.
  let until = 0;

  return {
    /** A chunk was just dropped because the film is on screen: this turn is now a film turn. */
    noteDroppedUnderFilm() {
      until = now() + ceilingMs;
    },

    /**
     * The film is gone — must this chunk still be dropped? True only for the turn that was already
     * being dropped under the film, and only until its boundary (or the backstop) lands.
     */
    shouldDropTail() {
      if (!until) return false;
      if (now() >= until) {
        until = 0;
        return false;
      }
      return true;
    },

    /**
     * A turn boundary arrived. Call it AFTER that event's own audio parts have been handled, never
     * before: native audio can hang a trailing audio part off the very event carrying the boundary,
     * and that part still belongs to the turn being closed.
     *
     * @returns {boolean} whether the gate was engaged (for tests / callers that care)
     */
    noteTurnBoundary() {
      const wasEngaged = until !== 0;
      until = 0;
      return wasEngaged;
    },

    /** A new socket: whatever turn was in flight died with the old one. */
    reset() {
      until = 0;
    },

    /** Introspection for tests; never a control path. */
    isEngaged() {
      return until !== 0;
    },
  };
}

export default createFilmTailGate;
